import { MainTitle } from 'components/MainTitle';
import { Layout } from 'features/Layouts/Layouts';
import { FC, useRef, useState } from 'react';
import { Affix, Box, Group, Loader, TextInput } from '@mantine/core';
import { Helmet } from 'react-helmet';
import styles from './ContractsContent.module.scss';
import { ReactComponent as IconPlus } from './img/iconPlus.svg';
import { ReactComponent as IconChevronLeft } from './img/iconChewronLeft.svg';
import { Button } from 'ui/Button';
import {
  getAuthorizationHeader,
  useGetContractsQuery,
  useGetFiltersContractsQuery,
} from 'redux/Api/contractsApi/contractsApi';
import { LoadingSkeleton } from 'components/LoadingSkeleton';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { ContractsList } from 'components/ContractsList';
import ContentLoader from 'react-content-loader';
import { ContractsListMobile } from 'components/ContractsListMobile';
import { Modal } from 'components/Modal';
import { CreateContractForm } from 'components/CreateContractForm';
import { useNavigate } from 'react-router-dom';
import { ModalConfirm } from 'components/ContractItem/ModalConfirm';
import { useMediaQuery, useToggle } from '@mantine/hooks';
import { useDownloadContract } from 'hooks/useDownloadContract';
import { useCanManage } from 'hooks/useCanManage';
import { InputSearch } from 'ui/InputSearch';
import { SortFilter } from './Sort/SortFilter';
import { Filters, CategoryItem } from './Filters/Filters';
import { ReactComponent as IconClose } from './img/iconClose.svg';
import { ChildrenType } from 'redux/Api/contractsApi/type';
import { MobileMenu } from './Sort/MobileMenu';
import { ReactComponent as IconSearchBtn } from './img/iconSearchBtn.svg';
import { ReactComponent as IconSortAsc } from '../../assets/img/filter/iconSortAsc.svg';
import { ReactComponent as IconSortDesc } from '../../assets/img/filter/iconSortDesc.svg';
import { ReactComponent as IconDownload } from './img/iconDownload2.svg';
import download from './img/iconDownload2.svg';
import { SuccessErrorInfo } from 'components/SuccessErrorInfo';
import ScrollButton from 'components/ScrollButton';
import { TitleRow } from 'components/TitleRow';
import useYandexMetrica from 'hooks/useYandexMetrica';
import { AppTooltip } from 'ui/AppTooltip';

export const ContractsContent: FC = () => {
  const { sendGoal } = useYandexMetrica();
  const canManage = useCanManage();
  const [showNewContract, setShowNewContract] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const navigate = useNavigate();
  const [contractData, setContractData] = useState<{ id: number; number: string } | null>(
    null,
  );
  const [searchValue, setSearchValue] = useState('');
  const [isSearchOpen, toggleSearchOpen] = useToggle();
  const { data: dataFilters, isLoading: isLoadingTabs } = useGetFiltersContractsQuery();
  const [isDownloading, setIsDownloading] = useState(false);
  const [textError, setTextError] = useState<string | null>(null);
  const [isLoadingDownloading, setIsLoadingDownloading] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const [activeSort_type, setActiveSort_type] = useState(
    localStorage.getItem('contractsSort_type') || 'default',
  );

  const [activeSort_direction, setActiveSort_direction] = useState(
    localStorage.getItem('contractsSort_direction') || 'asc',
  );
  const [mobileSearchClicked, setMobileSearchClicked] = useState(false);
  const { handleDownloadContract } = useDownloadContract({
    contractId: contractData?.id || null,
    contractNumber: contractData?.number || null,
    setIsLoadingDownloading,
  });

  const isMobile = useMediaQuery(`(width < 768px)`);
  const isMediumScreen = useMediaQuery('(width >= 768px)');
  const buttonTransform = useMediaQuery(`(min-width: 1200px)`);
  const isLessThan1595px = useMediaQuery('(width < 1595px)');
  const isLessThan1390px = useMediaQuery('(width < 1400px)');
  const [openNav, setOpenNav] = useState<boolean | undefined>(false);

  const saveFiltersToStorage = (filters: {
    nationalities?: string[];
    types?: string[];
    mvd_notifications?: string[];
    statuses?: string[];
    categories?: CategoryItem[];
    date_add?: { start: string; end: string };
  }) => {
    const toSave = {
      types: filters.types ?? [],
      mvd_notifications: filters.mvd_notifications ?? [],
      nationalities: filters.nationalities ?? [],
      categories: filters.categories ?? [],
      statuses: filters.statuses ?? [],
      date_add: filters.date_add,
    };
    localStorage.setItem('contractsFilters', JSON.stringify(toSave));
  };

  const handleApplyContractsFilters = (filters: {
    nationalities?: string[];
    types?: string[];
    mvd_notifications?: string[];
    statuses?: string[];
    categories?: CategoryItem[];
    date_add?: { start: string; end: string };
  }) => {
    const nextFilters = {
      types: filters.types ?? [],
      mvd_notifications: filters.mvd_notifications ?? [],
      nationalities: filters.nationalities ?? [],
      categories: filters.categories ?? [],
      statuses: filters.statuses ?? [],
      date_add: filters.date_add,
    };
    setParams((prev) => ({
      ...prev,
      page: 1,
      ...nextFilters,
    }));
    saveFiltersToStorage(nextFilters);
  };

  const handleResetContractsFilters = () => {
    const emptyFilters = {
      types: [] as string[],
      mvd_notifications: [] as string[],
      nationalities: [] as string[],
      categories: [] as CategoryItem[],
      statuses: [] as string[],
      date_add: undefined as { start: string; end: string } | undefined,
    };
    setParams((prev) => ({
      ...prev,
      page: 1,
      ...emptyFilters,
    }));
    saveFiltersToStorage(emptyFilters);
  };

  const normalizeStoredCategories = (categories: unknown): CategoryItem[] => {
    if (!Array.isArray(categories)) return [];
    if (categories.length === 0) return [];
    if (typeof categories[0] === 'string') {
      return (categories as string[]).map((id) => ({ id, name: id }));
    }
    return categories as CategoryItem[];
  };

  const loadFiltersFromStorage = (): {
    types: string[];
    mvd_notifications: string[];
    statuses: string[];
    nationalities: string[];
    categories: CategoryItem[];
    date_add?: { start: string; end: string };
  } => {
    try {
      const saved = localStorage.getItem('contractsFilters');
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          ...parsed,
          categories: normalizeStoredCategories(parsed.categories),
        };
      }
    } catch {
      // ignore parse errors
    }
    return {
      types: [],
      mvd_notifications: [],
      statuses: [],
      nationalities: [],
      categories: [],
    };
  };

  const savedFilters = loadFiltersFromStorage();

  const [params, setParams] = useState({
    page: 1,
    searchValue: '',
    sort_type:
      activeSort_type === 'default'
        ? undefined
        : activeSort_type === 'date_start'
        ? 'date_start'
        : activeSort_type === 'date_end'
        ? 'date_end'
        : activeSort_type === 'customer_signed_at'
        ? 'customer_signed_at'
        : activeSort_type === 'worker_signed_at'
        ? 'worker_signed_at'
        : activeSort_type === 'worker_fio'
        ? 'worker_fio'
        : activeSort_type === 'last_name_desc'
        ? 'last_name_desc'
        : undefined,
    sort_direction: activeSort_direction === 'asc' ? 'asc' : 'desc',
    statuses: savedFilters.statuses ?? [],
    mvd_notifications: savedFilters.mvd_notifications ?? [],
    types: savedFilters.types ?? [],
    nationalities: savedFilters.nationalities ?? [],
    categories: savedFilters.categories ?? [],
    date_add: savedFilters.date_add,
  });

  const contractsQueryParams = {
    page: params.page,
    searchValue: params.searchValue,
    sort_type: params.sort_type,
    nationalities: params.nationalities,
    categories: params.categories?.map((c) => c.id) ?? [],
    date_add: params.date_add,
    types: params.types,
    mvd_notifications: params.mvd_notifications,
    statuses: params.statuses,
    sort_direction: params.sort_type ? params.sort_direction : undefined,
  };
  const { data, isLoading, isFetching } = useGetContractsQuery(contractsQueryParams);

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      setParams((prev) => ({
        ...prev,
        page: 1,
        searchValue,
      }));
    }
  };

  const hasNextPage = data?.meta
    ? data?.meta?.last_page !== data?.meta?.current_page
    : false;

  const showFullPageSkeleton = isLoading || (isFetching && params.page === 1);

  const mobileSortTabs =
    dataFilters?.sort_type?.map((tab: { keyword: string; title: string }) => ({
      value: tab.keyword,
      label: tab.title,
    })) || [];

  const mobileSortTabsCustom = mobileSortTabs.flatMap((item) => [
    {
      value: item.value,
      label:
        item.label === 'Исполнители'
          ? `${item.label} (от А до Я)`
          : `${item.label} (по возрастанию)`,
      sort_direction: 'asc',
    },
    {
      value: item.value,
      label:
        item.label === 'Исполнители'
          ? `${item.label} (от Я до А)`
          : `${item.label} (по убыванию)`,
      sort_direction: 'desc',
    },
  ]);

  const hidePerformer = false;

  const dataTitle = [
    '№',
    'Дата начала',
    'Дата окончания',
    'Подписано заказчиком',
    'Подписано исполнителем',
    ...(!hidePerformer ? ['Исполнитель'] : []),
    'Договор',
    'Гражданство',
    'МВД',
    '',
  ];

  const activeSortName =
    mobileSortTabs.find(
      (tab: { value: string; label: string }) => tab.value === activeSort_type,
    )?.value || 'default';

  const newDataFiltersMobile = dataFilters
    ? transformFilters(dataFilters)
    : { statuses: [], mvd_notifications: [], nationalities: [] };

  const handleTabClickSort = (valSort: string, valDirection: string) => {
    localStorage.setItem('contractsSort_type', valSort);
    setActiveSort_type(valSort);
    if (valSort === 'default') {
      localStorage.setItem('contractsSort_direction', 'asc');
      setActiveSort_direction('asc');
    } else {
      localStorage.setItem('contractsSort_direction', valDirection);
      setActiveSort_direction(valDirection);
    }
    setParams((prev) => ({
      ...prev,
      page: 1,
      sort_type:
        valSort === 'default'
          ? undefined
          : valSort === 'date_start'
          ? 'date_start'
          : valSort === 'date_end'
          ? 'date_end'
          : valSort === 'customer_signed_at'
          ? 'customer_signed_at'
          : valSort === 'worker_signed_at'
          ? 'worker_signed_at'
          : valSort === 'worker_fio'
          ? 'worker_fio'
          : valSort === 'last_name_desc'
          ? 'last_name_desc'
          : undefined,
      sort_direction: valSort === 'default' ? 'asc' : valDirection,
    }));
  };

  const handleDownload = async () => {
    sendGoal('ExportAllContracts');
    setIsDownloading(true);
    try {
      const responseFile = await fetch(
        `${process.env.REACT_APP_API_URL}/api/customer/docs/unified-contracts/export-to-excel`,
        {
          method: 'POST',
          headers: {
            Authorization: getAuthorizationHeader(),
          },
        },
      );

      const { file_id } = await responseFile.json();

      const responseToken = await fetch(
        `${process.env.REACT_APP_API_URL}/api/customer/docs/download/one-time-token`,
        {
          method: 'GET',
          headers: {
            Authorization: getAuthorizationHeader(),
          },
        },
      );

      const { token } = await responseToken.json();

      const url = `${process.env.REACT_APP_API_URL}/api/customer/docs/unified_contracts_xlsx/${file_id}/download/${token}`;
      window.open(url, '_self');
    } catch (error) {
      setTextError('Произошла ошибка при скачивании отчета');
      throw error;
    } finally {
      setIsDownloading(false);
    }
  };

  const handleToggleSortDirection = () => {
    const nextDirection = activeSort_direction === 'asc' ? 'desc' : 'asc';
    localStorage.setItem('contractsSort_direction', nextDirection);
    setActiveSort_direction(nextDirection);
    setParams((prev) => ({
      ...prev,
      page: 1,
      sort_direction: nextDirection,
    }));
  };

  const [intersectionRef] = useInfiniteScroll({
    loading: isLoading,
    hasNextPage,
    onLoadMore: () => setParams((prev) => ({ ...prev, page: prev.page + 1 })),
    rootMargin: '0px 0px 160px 0px',
  });

  const handleMobileSearch = () => {
    if (searchValue === '') {
      setParams((prev) => ({
        ...prev,
        page: 1,
        searchValue: '',
      }));
    } else {
      setParams((prev) => ({
        ...prev,
        searchValue,
      }));
    }
    setMobileSearchClicked(true);
  };

  const handleMobileSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    setMobileSearchClicked(false);

    if (e.target.value === '') {
      setParams((prev) => ({
        ...prev,
        page: 1,
        searchValue: '',
      }));
    }
  };

  return (
    <Layout setOpenNav={setOpenNav}>
      <Helmet>
        <title>Договоры с исполнителями</title>
      </Helmet>

      {isMobile ? (
        <Box className={styles.root}>
          <div className={styles.stickyHeader} style={{ padding: 0 }}>
            {isSearchOpen ? (
              <TextInput
                flex={1}
                mb='10px'
                placeholder='Поиск'
                type='search'
                autoFocus
                ref={searchRef}
                radius='8px'
                value={searchValue}
                styles={{ input: { height: '48px', border: 'none' } }}
                leftSection={
                  <IconChevronLeft
                    style={{ width: '25px', height: '25px' }}
                    onClick={() => {
                      if (isFetching) return;
                      setSearchValue('');
                      setMobileSearchClicked(false);
                      setParams((prev) => ({
                        ...prev,
                        searchValue: '',
                      }));
                      toggleSearchOpen();
                    }}
                  />
                }
                rightSection={
                  <Group gap='8px' mr='6'>
                    {searchValue !== '' && (
                      <IconClose
                        style={{ width: '16px', height: '16px' }}
                        onClick={() => {
                          if (isFetching) return;
                          setSearchValue('');
                          setMobileSearchClicked(false);
                          setParams((prev) => ({
                            ...prev,
                            searchValue: '',
                          }));
                          searchRef?.current?.focus();
                        }}
                      />
                    )}
                    {!mobileSearchClicked && (
                      <Box
                        style={{
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                        onClick={handleMobileSearch}>
                        <IconSearchBtn style={{ width: '71px', height: '40px' }} />
                      </Box>
                    )}
                  </Group>
                }
                rightSectionWidth={
                  searchValue !== '' && !mobileSearchClicked
                    ? 110
                    : mobileSearchClicked
                    ? 50
                    : 86
                }
                onChange={handleMobileSearchChange}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    setParams((prev) => ({
                      ...prev,
                      page: 1,
                      searchValue,
                    }));
                    setMobileSearchClicked(true);
                  }
                }}
              />
            ) : (
              <MainTitle
                title='Договоры с исполнителями'
                count={data?.meta?.total || 0}
              />
            )}

            {isSearchOpen ? null : isLoadingTabs || showFullPageSkeleton ? (
              <ContentLoader
                speed={2}
                height={34}
                backgroundColor={'#FFFFFF'}
                foregroundColor={'#D8E4FB8C'}
                style={{ borderRadius: '8px', marginBottom: '12px' }}>
                <rect x='0' y='0' rx='0' ry='0' width='100%' height='100%' />
              </ContentLoader>
            ) : (
              <div style={{ display: 'flex', gap: '12px' }}>
                <MobileMenu
                  mobileTabsSort={mobileSortTabsCustom}
                  mobileTabs={[]}
                  handleTabClickSort={handleTabClickSort}
                  activeSortName={activeSortName}
                  activeSortDirection={activeSort_direction}
                  setActiveSort_type={setActiveSort_type}
                  dataFiltersMobile={newDataFiltersMobile}
                  initialFilters={{
                    statuses: params.statuses ?? [],
                    mvd_notifications: params.mvd_notifications ?? [],
                    types: params.types ?? [],
                    nationalities: params.nationalities ?? [],
                    categories: params.categories ?? [],
                    date_add: params.date_add,
                  }}
                  onApplyFilters={handleApplyContractsFilters}
                  onResetFilters={handleResetContractsFilters}
                  toggleSearchOpen={toggleSearchOpen}
                />
              </div>
            )}
          </div>
          {showFullPageSkeleton ? (
            <ContentLoader
              speed={2}
              height={34}
              backgroundColor={'#FFFFFF'}
              foregroundColor={'#D8E4FB8C'}
              style={{ borderRadius: '8px', marginBottom: '12px' }}>
              <rect x='0' y='0' rx='0' ry='0' width='100%' height='100%' />
            </ContentLoader>
          ) : (
            <ContractsListMobile
              data={data?.data}
              intersectionComponent={
                hasNextPage && isMobile ? <div ref={intersectionRef} /> : null
              }
              key={data?.meta?.total}
            />
          )}
          {canManage ? (
            <Affix
              hiddenFrom='sm'
              position={{ bottom: 34, right: 0, left: 0 }}
              w='fit-content'
              m='0 auto'
              style={{ display: 'flex', justifyContent: 'center' }}>
              <Group gap='12px' wrap='nowrap'>
                <Box
                  p='12px'
                  bg='#002CFB'
                  h='48px'
                  w='214px'
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    color: 'white',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                  onClick={() => navigate('/contracts/create')}>
                  Создать договор <IconPlus />
                </Box>
                <ScrollButton isMobile={isMobile} />
              </Group>
            </Affix>
          ) : (
            <Affix
              hiddenFrom='sm'
              position={{ bottom: 34, right: 34 }}
              w='fit-content'
              m='0 auto'
              style={{ display: 'flex', justifyContent: 'center' }}>
              <ScrollButton isMobile={isMobile} />
            </Affix>
          )}
        </Box>
      ) : null}

      {isMediumScreen ? (
        <Box className={styles.root} visibleFrom='sm' mt={-20}>
          <div className={styles.stickyHeader}>
            <div className={styles.root_title}>
              <MainTitle
                title='Договоры с исполнителями'
                count={data?.meta?.total || 0}
              />
              {canManage && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {buttonTransform ? (
                    <Button
                      onClick={isDownloading ? undefined : handleDownload}
                      className={styles.root_title_btn}
                      theme='outline'>
                      <div>
                        Выгрузить в Excel{' '}
                        <div style={{ width: '20px', height: '20px' }}>
                          {isDownloading ? (
                            <Loader size='xs' color='#002CFB' />
                          ) : (
                            <IconDownload width={20} height={20} />
                          )}
                        </div>
                      </div>
                    </Button>
                  ) : (
                    <AppTooltip label={`Выгрузить в Excel`}>
                      <button
                        onClick={isDownloading ? undefined : handleDownload}
                        className={styles.headerBtn}>
                        {isDownloading ? (
                          <Loader size='xs' color='#002CFB' />
                        ) : (
                          <img
                            src={download}
                            alt='Выгрузить в Excel'
                            style={{ width: '20px', height: '20px' }}
                          />
                        )}
                      </button>
                    </AppTooltip>
                  )}
                  <Button
                    onClick={() => setShowNewContract(true)}
                    className={styles.root_title_btn}
                    theme='filed'>
                    <div>
                      Создать договор <IconPlus />
                    </div>
                  </Button>
                </div>
              )}
            </div>
            <div className={styles.root_search}>
              <div className={styles.root_search_item}>
                <InputSearch
                  notFocus
                  style={{ width: '260px', height: '48px' }}
                  value={searchValue}
                  onKeyDown={handleKeyDown}
                  onSearch={() => {
                    setParams((prev) => ({
                      ...prev,
                      page: 1,
                      searchValue: searchValue,
                    }));
                  }}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setSearchValue(e.target.value);

                    if (e.target.value === '') {
                      setParams((prev) => ({
                        ...prev,
                        page: 1,
                        searchValue: '',
                      }));
                    }
                  }}
                />
              </div>
              <div>
                {isLoadingTabs ? (
                  <ContentLoader
                    speed={2}
                    height={'44px'}
                    width={'185px'}
                    backgroundColor={'#FFFFFF'}
                    foregroundColor={'#D8E4FB8C'}>
                    <rect x='0' y='0' rx='0' ry='0' width='100%' height='100%' />
                  </ContentLoader>
                ) : (
                  <div style={{ display: 'flex' }}>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      {activeSort_type !== 'default' ? (
                        <Button
                          theme='outline'
                          className={styles.sort}
                          onClick={handleToggleSortDirection}>
                          <div className={styles.sort}>
                            {activeSort_direction === 'asc' ? (
                              <IconSortAsc />
                            ) : (
                              <IconSortDesc />
                            )}
                          </div>
                        </Button>
                      ) : null}

                      <SortFilter
                        lists={dataFilters?.sort_type || []}
                        active={activeSort_type}
                        setActive={(tab: string) => {
                          setActiveSort_type(tab);
                          if (tab === 'default') {
                            localStorage.setItem('contractsSort_direction', 'asc');
                            setActiveSort_direction('asc');
                          }
                          setParams((prev) => ({
                            ...prev,
                            page: 1,
                            sort_type:
                              tab === 'default'
                                ? undefined
                                : tab === 'date_start'
                                ? 'date_start'
                                : tab === 'date_end'
                                ? 'date_end'
                                : tab === 'customer_signed_at'
                                ? 'customer_signed_at'
                                : tab === 'worker_signed_at'
                                ? 'worker_signed_at'
                                : tab === 'worker_fio'
                                ? 'worker_fio'
                                : undefined,
                            sort_direction:
                              tab === 'default' ? 'asc' : prev.sort_direction,
                          }));
                        }}
                        localStorageKey='contractsSort_type'
                        isLoading={isFetching || isLoading}
                      />
                    </div>
                    <Filters
                      dataFilters={
                        dataFilters ?? {
                          nationalities: [],
                          statuses: [],
                          mvd_notifications: [],
                        }
                      }
                      initialFilters={{
                        statuses: params.statuses ?? [],
                        types: params.types ?? [],
                        mvd_notifications: params.mvd_notifications ?? [],
                        nationalities: params.nationalities ?? [],
                        categories: params.categories ?? [],
                        date_add: params.date_add,
                      }}
                      onApplyFilters={handleApplyContractsFilters}
                      onResetFilters={handleResetContractsFilters}
                      isLoading={isFetching || isLoading}
                    />
                  </div>
                )}
              </div>
            </div>
            {!((!openNav && isLessThan1595px) || isLessThan1390px) && (
              <div style={{ backgroundColor: '#fff', borderRadius: '8px 8px 0 0' }}>
                <TitleRow
                  data={dataTitle}
                  title={'contracts'}
                  hidePerformer={hidePerformer}
                />
              </div>
            )}
          </div>
          {showFullPageSkeleton ? (
            <LoadingSkeleton />
          ) : (
            <>
              {(!openNav && isLessThan1595px) || isLessThan1390px ? (
                <ContractsListMobile
                  data={data?.data}
                  intersectionComponent={
                    hasNextPage && !isMobile ? <div ref={intersectionRef} /> : null
                  }
                  key={data?.meta?.total}
                />
              ) : (
                <ContractsList
                  data={data?.data}
                  intersectionComponent={
                    hasNextPage && !isMobile ? <div ref={intersectionRef} /> : null
                  }
                  hideTitleRow
                  key={data?.meta?.total}
                />
              )}
            </>
          )}
          <Affix position={{ bottom: 50, left: openNav ? 80 : 286 }} zIndex={1000}>
            <ScrollButton />
          </Affix>
        </Box>
      ) : null}

      {!isLoading && isFetching && params.page > 1 && data?.data?.length > 0 && (
        <div
          className={styles.loaders}
          style={{ paddingBottom: isMobile ? '150px' : '25px' }}>
          <Loader size='xs' color='#002cfb' />
        </div>
      )}

      {showNewContract && (
        <Modal setOpen={setShowNewContract} open={showNewContract}>
          <CreateContractForm
            onComplete={({ id, number }) => {
              setShowNewContract(false);
              setShowConfirmModal(true);
              setContractData({ id, number });
            }}
          />
        </Modal>
      )}

      {showConfirmModal && (
        <ModalConfirm
          setShowLogoutModal={setShowConfirmModal}
          title='Договор создан'
          onConfirm={() => {
            setShowNewContract(false);
          }}
          text='Договор отправлен на подпись исполнителю в приложение'
          subText={
            <span
              className={styles.root_subText}
              onClick={() => {
                handleDownloadContract();
                setIsLoadingDownloading(true);
              }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                Договор {contractData?.number}
                {isLoadingDownloading && <Loader size='xs' color='#002cfb' />}
              </div>
            </span>
          }
          cancelText='Готово'
        />
      )}
      {textError && (
        <SuccessErrorInfo
          type='error'
          text={textError}
          handleClose={() => setTextError(null)}
        />
      )}
    </Layout>
  );
};

const transformFilters = (data: {
  nationalities: ChildrenType[];
  types?: ChildrenType[];
  mvd_notifications: ChildrenType[];
  statuses: ChildrenType[];
}) => {
  return {
    types: data?.types?.length
      ? data.types.map((item, i) => ({
          id: i + 1,
          keyword: item.keyword,
          label: item.title,
        }))
      : undefined,
    statuses: (data?.statuses ?? []).map((item, i) => ({
      id: i + 1,
      keyword: item.keyword,
      label: item.title,
    })),
    mvd_notifications: (data?.mvd_notifications ?? []).map((item, i) => ({
      id: i + 1,
      keyword: item.keyword,
      label: item.title,
    })),
    nationalities: (data?.nationalities ?? []).map((item, i) => ({
      id: i + 1,
      keyword: item.keyword,
      label: item.title,
    })),
  };
};
